import type { LoggingEventBus } from './LoggingEventBus';
import type { BasePermission, ComponentLogger, EmptyObject } from './loggingTypes';

/**
 * Produces a wrapper around the `logger` with a simplified interface that's similar to simple
 * loggers that have a logging level and that's it.
 *
 * The `component` is a required field for `LoggingEventBus#logEvent` so this conveniently sets it
 * here for you.
 *
 * NOTE: DO NOT make this more complex. For more advanced use-cases use the `logger` directly!
 *
 * Usage:
 *
 *     const logBus = new LoggingEventBus(new ConsoleEventListener());
 *     const logger = withComponent(logBus, 'my-component');
 *     logger.enable();
 */
export function withComponent<
  LoggingContext extends EmptyObject = EmptyObject,
  CustomEvent extends EmptyObject = EmptyObject,
  Permission extends BasePermission = BasePermission
>(
  logger: LoggingEventBus<LoggingContext, CustomEvent, Permission>,
  component: string
): ComponentLogger {
  return {
    enable: () => void logger.allow('logging'),
    disable: () => void logger.deny('logging'),
    debug: (message: string) => logger.logDebug({ component, message }),
    // NOTE: This goes to logDebug on purpose. See comment on the interface.
    info: (message: string) => logger.logDebug({ component, message }),
    increment: (action: string, label?: string) => logger.logInfo({ component, action, label }),
    warn: (message: string) => logger.logWarning({ component, message }),
    error: (error: unknown, message?: string) => logger.logError({ component, error, message }),
    timing: (variable: string, valueMs: number) =>
      logger.logTiming({ component, variable, valueMs }),
    value: (variable: string, value: number) => logger.logValue({ component, variable, value }),
  };
}
